import { onUnmounted, watch, type Ref } from 'vue'
import {
  chatOpenJumpLog,
  summarizeJumpScroll,
} from '@/modules/chat-matrix-fast/utils/chatOpenJumpLog'
import { scrollChatLogToBottom } from '@/modules/chat-matrix-fast/utils/scrollToChatMessage'

const NEAR_BOTTOM_PX = 120
const OPEN_STICK_MS = 1200
const HYDRATE_EXTEND_MS = 600

type TimelineSignal = {
  chatId: string | number | null
  count: number
  lastKey: string | null
}

export function useChatLogStickToBottom(scrollContainerRef: Ref<HTMLElement | null>) {
  let pinnedToBottom = true
  let stickUntilMs = 0
  let resizeObserver: ResizeObserver | null = null
  let releaseTimer: ReturnType<typeof setTimeout> | null = null
  let stickPassesTimer: ReturnType<typeof setTimeout> | null = null
  let applyingStick = false
  let lastStickLogAt = 0

  const getElement = (): HTMLElement | null => scrollContainerRef.value

  const measureNearBottom = (threshold = NEAR_BOTTOM_PX): boolean => {
    const el = getElement()
    if (!el) {
      return false
    }
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
  }

  const stopResizeStick = (): void => {
    resizeObserver?.disconnect()
    resizeObserver = null
    if (releaseTimer) {
      clearTimeout(releaseTimer)
      releaseTimer = null
    }
  }

  const releaseStickWindow = (): void => {
    stickUntilMs = 0
    stopResizeStick()
  }

  const stickOnce = (reason: string): void => {
    const el = getElement()
    if (!el) {
      return
    }
    const before = summarizeJumpScroll(el)
    applyingStick = true
    scrollChatLogToBottom(el)
    applyingStick = false
    pinnedToBottom = true
    const after = summarizeJumpScroll(el)
    const now = Date.now()
    if (now - lastStickLogAt > 250) {
      lastStickLogAt = now
      chatOpenJumpLog('stick · apply', { reason, before, after })
    }
  }

  const stickNow = (reason = 'stickNow'): void => {
    if (!pinnedToBottom && Date.now() > stickUntilMs) {
      return
    }
    stickOnce(`${reason}:sync`)
    queueMicrotask(() => {
      if (pinnedToBottom || Date.now() <= stickUntilMs) {
        stickOnce(`${reason}:micro`)
      }
    })
    requestAnimationFrame(() => {
      if (pinnedToBottom || Date.now() <= stickUntilMs) {
        stickOnce(`${reason}:raf`)
      }
    })
    if (stickPassesTimer) {
      clearTimeout(stickPassesTimer)
    }
    stickPassesTimer = setTimeout(() => {
      if (pinnedToBottom || Date.now() <= stickUntilMs) {
        stickOnce(`${reason}:t80`)
      }
    }, 80)
  }

  const armResizeStick = (ms: number): void => {
    const el = getElement()
    const content = el?.querySelector('.chat-log') ?? el?.firstElementChild
    if (!el || !content || typeof ResizeObserver === 'undefined') {
      return
    }

    stopResizeStick()
    resizeObserver = new ResizeObserver(() => {
      if ((Date.now() <= stickUntilMs || pinnedToBottom) && measureNearBottom(200)) {
        stickOnce('resize')
      }
    })
    resizeObserver.observe(content)
    releaseTimer = setTimeout(() => {
      stopResizeStick()
    }, ms + 250)
  }

  const beginOpenStick = (ms = OPEN_STICK_MS): void => {
    pinnedToBottom = true
    stickUntilMs = Date.now() + ms
    chatOpenJumpLog('stick · beginOpen', {
      ms,
      scroll: summarizeJumpScroll(getElement()),
    })
    stickNow('open')
    armResizeStick(ms)
  }

  const onScroll = (): void => {
    if (applyingStick) {
      return
    }

    const nearBottom = measureNearBottom()
    pinnedToBottom = nearBottom

    // User scrolled away from bottom — stop fighting them.
    if (!nearBottom && Date.now() <= stickUntilMs) {
      chatOpenJumpLog('stick · user released', {
        scroll: summarizeJumpScroll(getElement()),
      })
      releaseStickWindow()
    }
  }

  const shouldStick = (): boolean => pinnedToBottom || Date.now() <= stickUntilMs

  const onTimelineSignal = (next: TimelineSignal, prev: TimelineSignal | undefined): void => {
    if (!next.lastKey) {
      return
    }

    chatOpenJumpLog('timeline · signal', {
      chatId: next.chatId,
      count: next.count,
      lastKey: next.lastKey,
      prevChatId: prev?.chatId ?? null,
      prevCount: prev?.count ?? null,
      prevLastKey: prev?.lastKey ?? null,
      pinnedToBottom,
      stickLeftMs: Math.max(0, stickUntilMs - Date.now()),
      scroll: summarizeJumpScroll(getElement()),
    })

    if (!prev || next.chatId !== prev.chatId || next.lastKey !== prev.lastKey) {
      beginOpenStick()
      return
    }

    if (next.count > prev.count && shouldStick()) {
      stickUntilMs = Math.max(stickUntilMs, Date.now() + HYDRATE_EXTEND_MS)
      stickNow('hydrate-count')
      armResizeStick(Math.max(HYDRATE_EXTEND_MS, stickUntilMs - Date.now()))
    }
  }

  watch(
    scrollContainerRef,
    (el, _prev, onCleanup) => {
      if (!el) {
        return
      }
      el.addEventListener('scroll', onScroll, { passive: true })
      onCleanup(() => {
        el.removeEventListener('scroll', onScroll)
      })
    },
    { immediate: true },
  )

  onUnmounted(() => {
    stopResizeStick()
    if (stickPassesTimer) {
      clearTimeout(stickPassesTimer)
    }
  })

  return {
    beginOpenStick,
    onTimelineSignal,
    stickNow,
    shouldStick,
  }
}
